import { EcheancePaiementDetailDto } from "api/payment-schedule";
import { BadgeVariant } from "components/display/EntityDetailsHeader";
import {
  User, Building2, FileText, Receipt, Landmark,
  AlertTriangle, CheckCircle, XCircle
} from "lucide-react";
import { LucideIcon } from "lucide-react";
import { FC, ReactNode, useEffect, useState } from "react";
import { paymentScheduleAPI } from "services/swagger-api";

// ============= Helpers =============

export const statusConfig: Record<string, { label: string; variant: BadgeVariant; bg: string; text: string; border: string }> = {
  Planifie: { label: "Planifié", variant: "orange", bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200" },
  Pending: { label: "En attente", variant: "orange", bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200" },
  Paid: { label: "Payé", variant: "green", bg: "bg-green-100", text: "text-green-800", border: "border-green-200" },
  Failed: { label: "Échoué", variant: "red", bg: "bg-red-100", text: "text-red-800", border: "border-red-200" },
  Cancelled: { label: "Annulé", variant: "gray", bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-200" },
  Refunded: { label: "Remboursé", variant: "gray", bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200" },
};

export function getStatusInfo(status: string) {
  return statusConfig[status] ?? { label: status, variant: "gray" as BadgeVariant, bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-200" };
}

export function formatDate(date?: Date | string | null): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatAmount(amount?: number | null): string {
  if (amount == null) return "-";
  return amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export function formatIban(iban?: string | null): string {
  if (!iban) return "-";
  return iban.replace(/(.{4})/g, "$1 ").trim();
}

// ============= Sub-components =============

export function InfoCard({ title, icon: Icon, children }: { title: string; icon?: LucideIcon; children: ReactNode }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 bg-primary/5 border-b">
        {Icon && (
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10">
            <Icon className="w-5 h-5 text-primary" />
          </div>
        )}
        <h2 className="text-base font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );
}

export function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value ?? "-"}</span>
    </div>
  );
}

export function StatusBadge({ status, statusColor }: { status: string; statusColor?: string | null }) {
  if (statusColor) {
    return (
      <span
        className="px-2 py-0.5 rounded-full text-xs font-medium border"
        style={{
          backgroundColor: statusColor + "20",
          color: statusColor,
          borderColor: statusColor + "40",
        }}
      >
        {status}
      </span>
    );
  }

  const config = getStatusInfo(status);
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
      {config.label}
    </span>
  );
}

// ============= Tab Component =============

interface PaymentScheduleInfoTabProps {
  paymentScheduleId: string;
}

export const PaymentScheduleInfoTab: FC<PaymentScheduleInfoTabProps> = ({ paymentScheduleId }) => {
  const [detail, setDetail] = useState<EcheancePaiementDetailDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await paymentScheduleAPI.getPaymentScheduleById({ paymentScheduleId });
        setDetail(data);
      } catch (err) {
        console.error("Erreur lors du chargement des détails:", err);
        setError("Impossible de charger les détails du prélèvement");
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetail();
  }, [paymentScheduleId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-gray-500">Chargement des informations...</p>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-red-500">{error || "Informations introuvables"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        <InfoCard title="Souscripteur" icon={User}>
          <InfoRow label="Nom" value={detail.subscriber.lastName ?? "-"} />
          <InfoRow label="Prénom" value={detail.subscriber.firstName ?? "-"} />
          <InfoRow label="Email" value={detail.subscriber.email ?? "-"} />
          <InfoRow label="Téléphone" value={detail.subscriber.phone ?? "-"} />
          {detail.subscriber.companyName && (
            <InfoRow label="Société" value={detail.subscriber.companyName} />
          )}
        </InfoCard>

        <InfoCard title="Courtier" icon={Building2}>
          <InfoRow label="Société" value={detail.broker.companyName ?? "-"} />
          <InfoRow label="Email" value={detail.broker.email ?? "-"} />
          <InfoRow label="Téléphone" value={detail.broker.phone ?? "-"} />
        </InfoCard>

        <InfoCard title="Contrat" icon={FileText}>
          <InfoRow label="N° Contrat" value={detail.contractNumber} />
          <InfoRow label="Produit" value={detail.productName} />
          <InfoRow label="Statut" value={<StatusBadge status={detail.contractStatus} />} />
          <InfoRow label="Date d'effet" value={formatDate(detail.contractEffectDate)} />
        </InfoCard>

        <InfoCard title="Quittance" icon={Receipt}>
          <InfoRow label="N° Quittance" value={detail.receiptNumber ?? "-"} />
          <InfoRow label="Début" value={formatDate(detail.receiptStartDate)} />
          <InfoRow label="Fin" value={formatDate(detail.receiptEndDate)} />
          <InfoRow label="Montant total" value={formatAmount(detail.receiptTotalAmount)} />
        </InfoCard>

        <InfoCard title="Informations bancaires" icon={Landmark}>
          <InfoRow
            label="Réglé par"
            value={detail.contractPayer === 'COURTIER' ? 'Le courtier (encaisseur)' : 'Le souscripteur'}
          />
          <InfoRow label="IBAN Débiteur" value={formatIban(detail.debtorIban)} />
          <InfoRow label="IBAN Créditeur" value={formatIban(detail.creditorIban)} />
          <InfoRow label="RUM" value={detail.rum ?? "-"} />
          <InfoRow label="Réf. SEPA" value={detail.sepaReference ?? "-"} />
          <InfoRow label="Clé prélèvement" value={detail.directDebitKey ?? "-"} />
          <InfoRow label="Signature mandat" value={detail.mandateSignatureDate ?? "-"} />
          <InfoRow label="Création prél." value={formatDate(detail.directDebitCreationDate)} />
          <InfoRow label="Type" value={detail.isExternal ? "Externe" : "Interne"} />
        </InfoCard>

        <InfoCard title="Impayés & Tentatives" icon={AlertTriangle}>
          <InfoRow label="Impayé" value={
            detail.hasUnpaid
              ? <span className="inline-flex items-center gap-1 text-red-600"><XCircle size={14} /> Oui</span>
              : <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle size={14} /> Non</span>
          } />
          <InfoRow label="Tentatives" value={detail.retryCount.toString()} />
          <InfoRow label="Dernière tentative" value={formatDate(detail.lastRetryDate)} />
          <InfoRow label="Dernier code retour" value={detail.lastReturnCode ?? "-"} />
          <InfoRow label="Dernière erreur" value={detail.lastErrorMessage ?? "-"} />
          <InfoRow label="Date impayé" value={formatDate(detail.unpaidDate)} />
          <InfoRow label="Code motif impayé" value={detail.unpaidReasonCode ?? "-"} />
          <InfoRow label="Motif rejet" value={detail.rejectionLabel ?? "-"} />
          <InfoRow label="Régularisation" value={formatDate(detail.regularizationDate)} />
        </InfoCard>
      </div>
    </div>
  );
};
